import { Mtds } from 'prove-common'

export type ProgressSegment = {
  /** Unik nøkkel, typisk ownerId. */
  id: string
  /** Navnet på enheten, vist når brukeren holder musepekeren over feltet. */
  name: string
  value: number
}

type SegmentedProgressProps = {
  segments: ProgressSegment[]
  max: number
  label: string
  /** Fordelt mer enn oppgaven har prøver. Da vises hele linjen som en advarsel. */
  isOverAllocated?: boolean
}

/**
 * Framdriftsindikator delt i ett felt per enhet, med et grått felt til slutt for
 * prøvene som ikke er fordelt. Feltene har samme farge – oppdelingen viser hvor
 * mange enheter prøvene er fordelt på, ikke hvilken enhet som er hvilken.
 */
export const SegmentedProgress = ({
  segments,
  max,
  label,
  isOverAllocated = false
}: Readonly<SegmentedProgressProps>) => {
  const visible = segments.filter((segment) => segment.value > 0)
  const total = visible.reduce((sum, segment) => sum + segment.value, 0)
  const remaining = Math.max(max - total, 0)
  const color = isOverAllocated
    ? 'var(--mtds-color-danger-base-default)'
    : 'var(--mtds-color-main-base-default)'

  return (
    <Mtds.Flex
      role='group'
      aria-label={label}
      data-gap={1}
      data-nowrap
      className='h-2 w-full'
    >
      {visible.map((segment) => (
        <div
          key={segment.id}
          tabIndex={0}
          data-tooltip={`${segment.name}: ${segment.value} prøver`}
          className='rounded-full'
          style={{ flex: `${segment.value} 1 0`, background: color }}
        />
      ))}
      {remaining > 0 && (
        <div
          tabIndex={0}
          data-tooltip={`Ufordelt: ${remaining} prøver`}
          className='bg-neutral-surface-tinted rounded-full'
          style={{ flex: `${remaining} 1 0` }}
        />
      )}
    </Mtds.Flex>
  )
}
